'use client'

import { DIALOG_BASE } from '@trustoo/components/modal/constants/dialogPresets'
import { usePlainDialog } from '@trustoo/components/modal/hooks/usePlainDialog'
import { ComponentPropsWithoutRef } from 'react'
import { twMerge } from 'tailwind-merge'

export interface DialogRootProps extends Omit<
    ComponentPropsWithoutRef<'dialog'>,
    'open' | 'onClose'
> {
    open: boolean
    /**
     * Requested when the user closes the dialog (close button `onClick`, backdrop click, Escape).
     * Flip `open` to `false` in response; the dialog then closes itself.
     */
    onClose: () => void
    closeOnBackdropClick?: boolean
}

/**
 * Dialog.Root - native `<dialog>` building block
 *
 * **Role in Compound Structure:**
 * The root of the Dialog compound component. Owns the native `<dialog>` element, with no enter/exit
 * animation (use `Dialog.AnimatedRoot` for that). Controlled via `open` + `onClose`: the close
 * button, backdrop click and Escape all call `onClose`; flip `open` in response.
 *
 * Purely presentational — all behaviour (open/close sync, scroll restore, Escape and backdrop
 * handling) lives in `usePlainDialog`. Stays style-light: only the neutral `DIALOG_BASE` surface
 * is applied; pass `className` for any app-specific look (see
 * `@trustoo/components/modal/constants/dialogPresets`). Forwards any extra native `<dialog>`
 * attributes (e.g. `data-*`) onto the element.
 */
const PlainDialog = ({
    open,
    onClose,
    closeOnBackdropClick = false,
    className,
    children,
    ...rest
}: DialogRootProps) => {
    const { dialogRef, onBackdropClick } = usePlainDialog({ open, onClose, closeOnBackdropClick })

    return (
        <dialog
            ref={dialogRef}
            {...rest}
            onClick={onBackdropClick}
            className={twMerge(DIALOG_BASE, className)}>
            {children}
        </dialog>
    )
}

export default PlainDialog
